import { CreativeProductInstruction, creativeProductInstructionOptions } from 'utils/product'
import { ProductKind, ProductSegment, ProductType } from 'constants/product'

import { FC } from 'react'
import { Nullable } from 'models/helpers'
import { ProductCategory } from 'models/product'
import { Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface Props {
  productCategory: Nullable<ProductCategory>
  productType: Nullable<ProductType>
  productKind: Nullable<ProductKind>
  productSegment: Nullable<ProductSegment>
  attributeX: Nullable<number>
  attributeY: Nullable<number>
  productQuantity: Nullable<number>
  /** Text color, matched to the host surface (gray guidelines page vs orange mandatory-instructions banner). */
  color?: string
}

/**
 * Single-line creative instruction: a bold "{kind} instructions:" prefix followed by the
 * product-kind-specific interpolated sentence (m<sup>2</sup> superscript handled by
 * CreativeProductInstruction). Replaces the former separate Attribute X / Attribute Y fields,
 * mirroring the legacy Retool one-liner. Renders nothing when the product info is incomplete
 * or no instruction template maps to the kind.
 */
export const ProductInstructionLine: FC<Props> = ({
  productCategory,
  productType,
  productKind,
  productSegment,
  attributeX,
  attributeY,
  productQuantity,
  color,
}) => {
  const { t } = useTranslation(['creative_guidelines', 'product_kind'])

  if (!productCategory || !productType || !productKind) return null

  const hasTemplate = !!creativeProductInstructionOptions(productCategory, productType, productKind, attributeX ?? 0, attributeY ?? 0, productQuantity ?? 1, productSegment ?? undefined)
  if (!hasTemplate) return null

  return (
    <Typography variant="text-md" color={color}>

      <strong>{t('creative_guidelines:product_instruction_prefix', { kind: t(`product_kind:${productKind}`) })}</strong>{' '}

      <CreativeProductInstruction
        category={productCategory}
        type={productType}
        kind={productKind}
        attributeX={attributeX ?? 0}
        attributeY={attributeY ?? 0}
        count={productQuantity ?? 1}
        segment={productSegment ?? undefined}
      />

    </Typography>
  )
}
